import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Alert, View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import type { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../../lib/supabase'
import { reportUnexpectedError } from '../../../lib/reportError'
import type { CashierStackParamList } from '../../../navigation/types'
import { ScreenContainer } from '../../shared/ScreenContainer'
import { ChangeCashierPinFlow } from '../changePin'
import { CashierHeader } from './CashierHeader'
import { useUnpairDevice } from './useUnpairDevice'

interface CashierPairedLayoutProps {
  children: ReactNode
  onChangeCashierPinPress?: () => void
}

interface CashierDeviceHeaderInfo {
  deviceName?: string
  branchName?: string
}

function getStringMetadata(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined
}

export function CashierPairedLayout({
  children,
  onChangeCashierPinPress,
}: CashierPairedLayoutProps) {
  const { t } = useTranslation()
  const navigation = useNavigation<NativeStackNavigationProp<CashierStackParamList>>()
  const unpairDeviceMutation = useUnpairDevice()
  const [headerInfo, setHeaderInfo] = useState<CashierDeviceHeaderInfo>({})
  const [isChangePinOpen, setIsChangePinOpen] = useState(false)

  useEffect(() => {
    let isMounted = true

    void supabase.auth.getUser().then(({ data }) => {
      if (!isMounted) return

      setHeaderInfo({
        deviceName: getStringMetadata(data.user?.app_metadata?.device_name),
        branchName: getStringMetadata(data.user?.app_metadata?.branch_name),
      })
    })

    return () => {
      isMounted = false
    }
  }, [])

  const handleChangeCashierPinPress = () => {
    onChangeCashierPinPress?.()
    setIsChangePinOpen(true)
  }

  const completeUnpair = async () => {
    await unpairDeviceMutation.mutateAsync()
    try {
      await supabase.auth.signOut({ scope: 'local' })
    } catch (err) {
      // The device is already unpaired server-side at this point — this is
      // just the local session cleanup failing, not a real unpair failure.
      // Report it but don't let it trigger the "unpair failed" alert below.
      reportUnexpectedError(err)
    } finally {
      navigation.reset({ index: 0, routes: [{ name: 'Pairing' }] })
    }
  }

  const handleUnpairDevicePress = () => {
    if (unpairDeviceMutation.isPending) return

    Alert.alert(
      t('cashierHeader.unpairConfirmTitle'),
      t('cashierHeader.unpairConfirmMessage'),
      [
        { text: t('cashierHeader.unpairCancelButton'), style: 'cancel' },
        {
          text: t('cashierHeader.unpairConfirmButton'),
          style: 'destructive',
          onPress: () => {
            void completeUnpair().catch(() => {
              Alert.alert(
                t('cashierHeader.unpairErrorTitle'),
                t('cashierHeader.unpairErrorMessage'),
              )
            })
          },
        },
      ],
    )
  }

  return (
    <ScreenContainer>
      <CashierHeader
        deviceName={headerInfo.deviceName}
        branchName={headerInfo.branchName}
        onChangeCashierPinPress={handleChangeCashierPinPress}
        onUnpairDevicePress={handleUnpairDevicePress}
      />
      <View className="flex-1">{children}</View>
      <ChangeCashierPinFlow
        visible={isChangePinOpen}
        onClose={() => setIsChangePinOpen(false)}
      />
    </ScreenContainer>
  )
}
